// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormField, RadioButtonGroup } from 'grommet'
import { useTranslation } from 'react-i18next'

import { TENANT_WORKSPACE_SCOPES } from '../constants'

import WorkspaceGroupMultiselect from './WorkspaceGroupMultiselect'

const CustomerScopeSelection = ({
  handleSelection,
  scopeOption,
  preselectWorkspaceGroups = [],
  scopeError,
  setApiError,
  hasMSPOneRoleAppScope,
  hasCustomerOneRoleAppScope,
  setHasConflictingMSPOneRoleAppRA = () => {},
  mspScopeSelected
}) => {
  const { t } = useTranslation(['common', 'iam'])
  const [showWkspGroupDropdown, setShowWkspGroupDropdown] = useState(
    scopeOption === TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
  )

  const customerWkspScopeOptions = [
    {
      label: t('iam:msp.workspace_scope_none'),
      value: TENANT_WORKSPACE_SCOPES?.NONE
    },
    {
      label: t('iam:msp.msp_all_customers', {
        workspaces: t('common:business_object.wkspc_plural')
      }),
      value: TENANT_WORKSPACE_SCOPES?.ALL
    },
    {
      label: t('iam:msp.msp_specific_wkspc_groups', {
        wkspcGroups: t('common:business_object.wkspc_group_plural')
      }),
      value: TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
    }
  ]

  return (
    <>
      <FormField
        required
        label={t('iam:msp.customer_workspace_scope', {
          workspace: t('common:business_object.wkspc')
        })}
        error={scopeError?.wkspcGroupScopeError}
        data-testid="customer-wksp-scope-radio-btns"
      >
        <RadioButtonGroup
          name="customer-workspace-scope"
          options={customerWkspScopeOptions}
          labelKey="label"
          valueKey="value"
          value={scopeOption}
          onChange={(event) => {
            scopeError?.clearError()
            handleSelection(event?.value, 'customerWorkspaceScope')
            if (
              event?.value === TENANT_WORKSPACE_SCOPES?.ALL ||
              event?.value ===
                TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
            ) {
              setShowWkspGroupDropdown(
                event?.value ===
                  TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
              )
            } else {
              setShowWkspGroupDropdown(false)
            }

            setShowWkspGroupDropdown(
              event?.value ===
                TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
            )

            if (
              (event?.value === TENANT_WORKSPACE_SCOPES?.ALL ||
                event?.value ===
                  TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS) &&
              hasCustomerOneRoleAppScope
            ) {
              setHasConflictingMSPOneRoleAppRA(true)
            }

            if (
              !mspScopeSelected &&
              hasMSPOneRoleAppScope &&
              event?.value === TENANT_WORKSPACE_SCOPES?.NONE
            ) {
              setHasConflictingMSPOneRoleAppRA(false)
            }

            if (
              event?.value === TENANT_WORKSPACE_SCOPES?.NONE &&
              hasCustomerOneRoleAppScope &&
              !mspScopeSelected &&
              hasMSPOneRoleAppScope
            ) {
              setHasConflictingMSPOneRoleAppRA(false)
            }

            if (
              event?.value === TENANT_WORKSPACE_SCOPES?.NONE &&
              hasCustomerOneRoleAppScope &&
              mspScopeSelected &&
              !hasMSPOneRoleAppScope
            ) {
              setHasConflictingMSPOneRoleAppRA(false)
            }

            if (
              event?.value === TENANT_WORKSPACE_SCOPES?.NONE &&
              !mspScopeSelected
            ) {
              setHasConflictingMSPOneRoleAppRA(false)
            }
          }}
        />
      </FormField>
      {showWkspGroupDropdown && (
        <WorkspaceGroupMultiselect
          setSelectedWorkspaceGroups={(workspaceGroups) => {
            handleSelection(workspaceGroups, 'workspaceGroupInstances')
          }}
          preselectWorkspaceGroups={preselectWorkspaceGroups}
          wkspcGroupScopeError={scopeError}
          setApiError={setApiError}
        />
      )}
    </>
  )
}

CustomerScopeSelection.propTypes = {
  handleSelection: PropTypes.func.isRequired,
  scopeOption: PropTypes.object.isRequired,
  preselectWorkspaceGroups: PropTypes.array,
  scopeError: PropTypes.object.isRequired,
  setApiError: PropTypes.func.isRequired,
  hasMSPOneRoleAppScope: PropTypes.bool,
  hasCustomerOneRoleAppScope: PropTypes.bool,
  setHasConflictingMSPOneRoleAppRA: PropTypes.func,
  mspScopeSelected: PropTypes.bool
}

export default CustomerScopeSelection
